"use client";

import React, { createContext, useContext, useEffect, useState } from "react";
import Cookies from "js-cookie";

export type CookiePreferences = {
    necessary: boolean; // strict, always true
    analytics: boolean;
    marketing: boolean;
};

type CookieConsentContextType = {
    hasConsentBeenAnswered: boolean;
    preferences: CookiePreferences;
    acceptAll: () => void;
    rejectNonEssential: () => void;
    savePreferences: (newPreferences: CookiePreferences) => void;
};

const defaultPreferences: CookiePreferences = {
    necessary: true,
    analytics: false,
    marketing: false,
};

const CookieConsentContext = createContext<CookieConsentContextType | undefined>(undefined);

export const CookieConsentProvider = ({ children }: { children: React.ReactNode }) => {
    const [hasConsentBeenAnswered, setHasConsentBeenAnswered] = useState(false);
    const [preferences, setPreferences] = useState<CookiePreferences>(defaultPreferences);

    useEffect(() => {
        // Load from cookie on mount
        const savedConsent = Cookies.get("cookie-consent");
        if (savedConsent) {
            try {
                const parsed = JSON.parse(savedConsent);
                setPreferences({ ...defaultPreferences, ...parsed });
                setHasConsentBeenAnswered(true);
            } catch (e) {
                console.error("Error parsing cookie consent", e);
            }
        }
    }, []);

    const saveToCookie = (newPrefs: CookiePreferences) => {
        // Ensure necessary is always true
        const toSave = { ...newPrefs, necessary: true };
        Cookies.set("cookie-consent", JSON.stringify(toSave), { expires: 365, path: "/" });
        setPreferences(toSave);
        setHasConsentBeenAnswered(true);
    };

    const acceptAll = () => {
        saveToCookie({ necessary: true, analytics: true, marketing: true });
    };

    const rejectNonEssential = () => {
        saveToCookie({ necessary: true, analytics: false, marketing: false });
    };

    const savePreferences = (newPrefs: CookiePreferences) => {
        saveToCookie(newPrefs);
    };

    return (
        <CookieConsentContext.Provider
            value={{
                hasConsentBeenAnswered,
                preferences,
                acceptAll,
                rejectNonEssential,
                savePreferences,
            }}
        >
            {children}
        </CookieConsentContext.Provider>
    );
};

export const useCookieConsent = () => {
    const context = useContext(CookieConsentContext);
    if (!context) {
        throw new Error("useCookieConsent must be used within a CookieConsentProvider");
    }
    return context;
};
